import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Alert, Input, Select, Switch } from '@dreamtree-org/twreact-ui';
import { useAppApi, useStableApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import { KIND_LABELS } from '@/components/ToolProviderCreateDialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import {
  CATEGORY_HELP, CATEGORY_OPTIONS, ASK_HELP, useToolActions, DestructiveNote,
  AutoWriteNote, BulkArmBar, OutboundAuthPanel, SecretPanel, isAskTool, isAutoWrite,
} from '@/components/toolProviderShared';

/**
 * Unified tool provider detail — all kinds (http/mcp/client) in one place.
 * The MCP path reuses the richer panels from the former McpServerDetail
 * (agents attachment, sync report, system prompt) so there is exactly one
 * detail screen for app_tool_providers regardless of kind. The platform's
 * own MCP server at /mcp (services/mcpServer.js) is unrelated — see
 * docs/PUBLIC-API.md — and is not referenced here.
 */

// ---- Shared helpers for http/client (simple table) ----

/** The library has no Textarea component; a raw one with the app's field styling is the
 *  established convention here (see ConnectionProvidersPage's FieldSpecEditor). */
function TextArea({ label, help, rows = 3, value, onChange, placeholder, mono }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label}</label>
      <textarea
        className={`w-full rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm ${mono ? 'font-mono text-xs' : ''}`}
        rows={rows}
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <p className="text-xs text-secondary-500 mt-1">{help}</p>}
    </div>
  );
}

/**
 * How the outbound connection is being made. Worth surfacing because the two differ in more
 * than speed: the SDK negotiates a session over four requests, while plain JSON-RPC is a
 * single POST and is the only thing that works against servers with no `initialize`.
 */
const TRANSPORT_LABELS = {
  sdk: 'Streamable HTTP',
  jsonrpc: 'JSON-RPC (no handshake)',
};

/** Alert tone for a sync result. A server that lists its tools but refuses to run them is a
 *  warning, not a success — everything else in the report looks healthy while every agent
 *  call would fail. */
function syncVariant({ rejected, removed, drifted, callCheck }) {
  const callsFail = !!callCheck && callCheck.checked && !callCheck.ok;
  if (rejected.length || drifted || callsFail) return 'warning';
  if (removed.length) return 'info';
  return 'success';
}

/**
 * What the sync learned about actually RUNNING a tool, as opposed to listing one.
 *
 * Listing proves only that the endpoint answered — a server can hand over its whole manifest
 * to any caller and still refuse every invocation. Saying so here is the difference between
 * arming 73 tools that work and arming 73 that fail on first use.
 */
function CallCheckNote({ callCheck }) {
  if (!callCheck) return null;
  if (!callCheck.checked) {
    return (
      <p className="text-xs text-secondary-500">
        No read-only tool without required arguments, so calls could not be verified — only the
        listing is proven.
      </p>
    );
  }
  if (callCheck.ok) {
    return (
      <p className="text-xs text-secondary-500">
        Verified by calling <span className="font-mono">{callCheck.tool}</span> — this server both
        lists and runs its tools.
      </p>
    );
  }
  return (
    <p>
      <strong>The tools listed, but running one failed.</strong> Calling{' '}
      <span className="font-mono text-xs">{callCheck.tool}</span> returned:{' '}
      <span className="font-mono text-xs">{callCheck.error}</span>. Arming these tools will give
      the agent capabilities that error on first use — fix the credential first.
    </p>
  );
}

/** One row of the HTTP provider table. The per-tool writes and the
 *  category-redeclaration rule come from components/toolProviderShared.jsx.
 *  `selected`/`onToggleSelect` add the leading bulk-selection checkbox; when absent
 *  (no caller passes them today) the row renders exactly as before. */
function ToolRow({ tool, providerId, onChanged, onError, selected, onToggleSelect }) {
  const { busyId, patch, changeCategory } = useToolActions(providerId, { onChanged, onError });
  const busy = busyId === tool.id;

  return (
    <tr className="border-b border-[var(--app-border)] align-top">
      {onToggleSelect && (
        <td className="py-2 pr-3">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label={`Select ${tool.name}`}
          />
        </td>
      )}
      <td className="py-2 pr-3">
        <div className="font-mono text-sm">{tool.name}</div>
        <div className="text-xs text-secondary-500 max-w-md">{tool.description}</div>
      </td>
      <td className="py-2 pr-3 min-w-[180px]">
        <Select value={tool.category} disabled={busy} onChange={(v) => changeCategory(tool, v)} options={CATEGORY_OPTIONS} allowClear={false} />
        <p className="text-[11px] text-secondary-500 mt-1 max-w-xs">{CATEGORY_HELP[tool.category]}</p>
        {isAutoWrite(tool) && <AutoWriteNote />}
        {!!tool.destructive && <DestructiveNote />}
      </td>
      <td className="py-2 pr-3 text-xs">{tool.http_method ? `${tool.http_method} ${tool.http_path || ''}` : '—'}</td>
      <td className="py-2 pr-3">
        <Switch checked={!!tool.approved} disabled={busy} onChange={(checked) => patch(tool, { approved: checked })} />
      </td>
      <td className="py-2 pr-3">
        <Switch
          checked={!!tool.enabled}
          disabled={busy || !tool.approved}
          onChange={(checked) => patch(tool, { enabled: checked })}
        />
      </td>
      <td className="py-2 text-xs text-secondary-500">
        {tool.run_count ? `${tool.success_count || 0}/${tool.run_count}` : 'never run'}
      </td>
    </tr>
  );
}

const BLANK_TOOL = {
  name: '', description: '', httpMethod: 'POST', httpPath: '', category: 'confirm',
  schemaText: '{\n  "type": "object",\n  "properties": {}\n}',
};
const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((v) => ({ value: v, label: v }));

function AddToolForm({ providerId, onAdded, onError }) {
  const { fetchApi } = useAppApi();
  const [form, setForm] = useState(BLANK_TOOL);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    let inputSchema;
    try {
      inputSchema = JSON.parse(form.schemaText || '{}');
    } catch {
      onError('Input schema is not valid JSON.');
      return;
    }
    try {
      await fetchApi('providerToolCreate', { providerId, ...form, inputSchema });
      setForm(BLANK_TOOL);
      onAdded();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    }
  };

  return (
    <Card className="p-3 space-y-3">
      <h2 className="font-semibold">Declare a tool</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="add_item" />
          <p className="text-xs text-secondary-500 mt-1">Lower-case letters, digits and underscores — no dots.</p>
        </div>
        <Select label="Category" value={form.category} onChange={(v) => set({ category: v })} options={CATEGORY_OPTIONS} allowClear={false} />
        <Select label="Method" value={form.httpMethod} onChange={(v) => set({ httpMethod: v })} options={METHOD_OPTIONS} />
        <Input label="Path" value={form.httpPath} onChange={(e) => set({ httpPath: e.target.value })} placeholder="/cart/items or /orders/{orderId}" />
      </div>
      <TextArea
        label="Description"
        rows={2}
        value={form.description}
        onChange={(v) => set({ description: v })}
        placeholder="What the model should understand this tool does."
      />
      <TextArea
        label="Input schema (JSON Schema)"
        rows={6}
        mono
        value={form.schemaText}
        onChange={(v) => set({ schemaText: v })}
        help="A prompt for the model, not a validator — always validate arguments on your side too."
      />
      <Button size="sm" onClick={submit}>Add tool</Button>
    </Card>
  );
}

function ToolTable({ tools, providerId, onChanged, onError }) {
  // Same selection contract as the MCP table below: ticked rows scope the bulk menu,
  // unticked means every listed tool. Pruned on reload so a deleted tool can't linger.
  const [selectedIds, setSelectedIds] = useState([]);
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.length) return prev;
      const alive = new Set(tools.map((t) => t.id));
      const next = prev.filter((id) => alive.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [tools]);
  const allIds = tools.map((t) => t.id);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allChecked ? [] : allIds);
  const toggleOne = (id) => setSelectedIds((prev) => (
    prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
  ));
  return (
    <Card className="p-3">
      <h2 className="font-semibold mb-3">Tools ({tools.length})</h2>
      <Can module="toolproviders" action="author">
        <BulkArmBar
          tools={tools}
          providerId={providerId}
          selectedIds={selectedIds}
          visibleIds={allIds}
          onChanged={onChanged}
          onError={onError}
          onClearSelection={() => setSelectedIds([])}
        />
      </Can>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-secondary-500 text-xs">
            <tr className="border-b border-[var(--app-border)]">
              <th className="py-2 pr-3 w-8">
                <input type="checkbox" className="rounded border-gray-300" checked={allChecked} onChange={toggleAll} aria-label="Select all tools" />
              </th>
              <th className="text-left py-2 pr-3">Tool</th>
              <th className="text-left py-2 pr-3">Category</th>
              <th className="text-left py-2 pr-3">Request</th>
              <th className="text-left py-2 pr-3">Approved</th>
              <th className="text-left py-2 pr-3">Enabled</th>
              <th className="text-left py-2">Runs</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((t) => (
              <ToolRow
                key={t.id}
                tool={t}
                providerId={providerId}
                onChanged={onChanged}
                onError={onError}
                selected={selectedIds.includes(t.id)}
                onToggleSelect={() => toggleOne(t.id)}
              />
            ))}
          </tbody>
        </table>
        {!tools.length && <p className="text-sm text-secondary-500 py-3">No tools declared yet.</p>}
      </div>
    </Card>
  );
}

// ---- MCP-specific helpers (ported from former McpServerDetail) ----

const FILTERS = [
  { value: 'all', label: 'All tools' },
  { value: 'armed', label: 'Armed (bound to agents)' },
  { value: 'pending', label: 'Awaiting approval' },
  { value: 'disabled', label: 'Approved but off' },
];

function applyFilter(tools, filter) {
  if (filter === 'armed') return tools.filter((t) => t.approved && t.enabled);
  if (filter === 'pending') return tools.filter((t) => !t.approved);
  if (filter === 'disabled') return tools.filter((t) => t.approved && !t.enabled);
  return tools;
}

function DedupeNote({ count }) {
  if (!count) return null;
  return <p>Merged {count} duplicate tool row(s) — each tool is now listed once.</p>;
}

function SyncReport({ result, onDismiss, providerId, tools, onChanged }) {
  const { fetchApi } = useAppApi();
  const [showAll, setShowAll] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  if (!result) return null;
  const { discovered, rejected = [], removed = [], drifted, callCheck, deduplicated } = result;
  const displayRejected = showAll ? rejected : rejected.slice(0, 5);
  const variant = syncVariant({ rejected, removed, drifted, callCheck });
  const handleCleanup = async () => {
    if (!providerId || !removed.length) return;
    setCleaning(true);
    try {
      const byName = new Map((tools || []).map((t) => [t.name, t]));
      for (const name of removed) {
        const row = byName.get(name);
        if (row) await fetchApi('providerToolDelete', { providerId, toolId: row.id }).catch(() => {});
      }
      if (onChanged) await onChanged();
      onDismiss();
    } finally {
      setCleaning(false);
    }
  };
  return (
    <Alert variant={variant} dismissible onDismiss={onDismiss}
      title={`Reconnected — ${discovered} tool(s) discovered`}>
      <div className="space-y-1 text-sm">
        <CallCheckNote callCheck={callCheck} />
        <DedupeNote count={deduplicated} />
        {drifted && (
          <p>
            The server’s manifest changed since it was approved. Tools whose description or
            schema moved were un-approved and need reviewing again — that text is what the
            model reads as instructions.
          </p>
        )}
        {removed.length > 0 && (
          <div className="flex flex-wrap items-start gap-2">
            <p className="flex-1">No longer offered, so disabled: <span className="font-mono text-xs">{removed.join(', ')}</span></p>
            {providerId && (
              <Button size="sm" variant="secondary" disabled={cleaning} onClick={handleCleanup}>
                {cleaning ? 'Cleaning…' : `Delete ${removed.length} stale`}
              </Button>
            )}
          </div>
        )}
        {rejected.length > 0 && (
          <div>
            <p>Rejected — these can never be bound: {rejected.length > 5 && !showAll ? `showing 5 of ${rejected.length}` : ''}</p>
            <ul className="list-disc ml-5 text-xs">
              {displayRejected.map((r) => <li key={r.name}><span className="font-mono">{r.name}</span> — {r.reason}</li>)}
            </ul>
            {rejected.length > 5 && (
              <Button size="sm" variant="secondary" className="mt-2" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show less' : `Show all ${rejected.length}`}
              </Button>
            )}
            <p className="text-xs text-secondary-500 mt-1">Depth limit is now 15 logical levels (properties/items nesting — descriptions, enums and examples don’t count). If a tool still shows “deeper than 15”, flatten its input schema on the MCP server (e.g., avoid deeply nested filter objects).</p>
          </div>
        )}
      </div>
    </Alert>
  );
}

/** Tool-catalog knowledge graph status (GET .../intelligence) + on-demand rebuild.
 *  The graph is what turn-time belt selection traverses from the top-cosine
 *  tools to entity-linked ones; it rebuilds automatically after each sync, and
 *  this card surfaces (and manually recovers) it. Fetch-once + manual refresh,
 *  mirroring the KB themes panel — no polling. */
function ToolGraphCard({ providerId }) {
  const { fetchApi } = useStableApi();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(() => fetchApi('toolProviderIntelligence', providerId)
    .then((res) => { setInfo(res); setErr(''); })
    .catch((ex) => setErr(ex?.response?.data?.error || ex.message)), [providerId]);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setBusy(true);
    setNote('');
    try {
      const res = await fetchApi('toolProviderGraphRebuild', providerId);
      setNote(res && res.started === false
        ? 'A rebuild is already running — refresh to check progress.'
        : 'Rebuilding in the background — refresh to check progress.');
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const graph = info?.graph;
  return (
    <Card className="p-3 space-y-2">
      <h2 className="font-semibold">Tool graph</h2>
      {err && <p className="text-xs text-red-600">{err}</p>}
      {!err && !info && <p className="text-xs text-secondary-500">Loading…</p>}
      {info && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500">
            {graph && graph.nodes > 0 ? (
              <>
                <Badge variant="success" size="sm">{graph.nodes} nodes</Badge>
                <Badge variant="secondary" size="sm">{graph.semantic ? 'semantic entities' : 'structural only'}</Badge>
              </>
            ) : (
              <span>No tool graph yet — it builds automatically after each sync.</span>
            )}
          </div>
          <p className="text-xs text-secondary-500">
            Turn-time selection expands each provider&apos;s top tools across this graph, so tools
            sharing a concept surface together.
          </p>
        </>
      )}
      <div className="flex items-center gap-2">
        <Can module="toolproviders" action="author">
          <Button size="sm" variant="outline" onClick={rebuild} disabled={busy}>
            {busy ? 'Rebuilding…' : 'Rebuild graph'}
          </Button>
        </Can>
        {busy && <span className="text-xs text-secondary-500">Running in the background…</span>}
        {!busy && note && <span className="text-xs text-secondary-500">{note}</span>}
        {!busy && !note && (
          <button onClick={load} className="text-xs text-secondary-500 hover:text-secondary-700">Refresh status</button>
        )}
      </div>
    </Card>
  );
}

function SystemPromptPanel({ id, agents, onAttachError, onAgentsChanged }) {
  const { fetchApi } = useAppApi();
  const [includePending, setIncludePending] = useState(false);
  const [state, setState] = useState({ prompt: '', armedCount: 0, totalCount: 0 });
  const [copied, setCopied] = useState(false);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    fetchApi('toolProviderSystemPrompt', { id, includePending })
      .then(setState)
      .catch(() => setState({ prompt: '', armedCount: 0, totalCount: 0 }));
  }, [id, includePending]);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setNote('Could not reach the clipboard — select the text and copy manually.');
    }
  };

  const appendToAgent = async () => {
    const agent = agents.find((a) => String(a.id) === String(target));
    if (!agent) return;
    setBusy(true);
    setNote('');
    try {
      const existing = agent.system_prompt_template || '';
      const next = existing.trim() ? `${existing.trim()}\n\n${state.prompt}` : state.prompt;
      await fetchApi('crud', {
        model: 'AppAiAgent', action: 'update', where: { id: agent.id }, data: { system_prompt_template: next },
      });
      setNote(`Appended ${state.prompt.length} characters to “${agent.name}” — its prompt is now ${next.length} characters.`);
      onAgentsChanged();
    } catch (ex) {
      onAttachError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">System prompt for this server</h2>
          <p className="text-xs text-secondary-500">
            Generated from the {state.armedCount} armed tool(s) — their real names, arguments and
            approval rules — so an agent knows what it can call instead of guessing.
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>

      {state.totalCount - state.armedCount > 0 ? (
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={includePending} onChange={setIncludePending} />
          Also list the {state.totalCount - state.armedCount} tool(s) that aren&apos;t armed (marked as not callable)
        </label>
      ) : (
        <p className="text-xs text-secondary-500">Every tool on this server is armed, so the prompt already lists all of them.</p>
      )}

      <textarea
        className="w-full rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 font-mono text-xs"
        rows={12}
        readOnly
        value={state.prompt}
      />

      <Can module="agents" action="govern">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-64">
            <Select
              label="Append to an agent's system prompt"
              options={[{ value: '', label: 'Select an agent…' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]}
              value={target}
              onChange={setTarget}
              searchable
            />
          </div>
          <Button size="sm" disabled={!target || busy || !state.prompt} onClick={appendToAgent}>Append</Button>
        </div>
      </Can>
      {note && <p className="text-xs text-secondary-500">{note}</p>}
    </Card>
  );
}

function AgentsPanel({ id, agents, armedCount, onChanged, onError }) {
  const { fetchApi } = useAppApi();
  const [busy, setBusy] = useState(false);
  const numericId = Number(id);

  const parseIds = (agent) => {
    try {
      const value = JSON.parse(agent.tool_provider_ids || 'null');
      return Array.isArray(value) ? value.map(Number) : [];
    } catch { return []; }
  };

  const attached = agents.filter((a) => parseIds(a).includes(numericId));

  const setAttachment = async (agent, shouldAttach) => {
    setBusy(true);
    try {
      const ids = parseIds(agent).filter((v) => v !== numericId);
      if (shouldAttach) ids.push(numericId);
      await fetchApi('crud', {
        model: 'AppAiAgent',
        action: 'update',
        where: { id: agent.id },
        data: { tool_provider_ids: ids.length ? JSON.stringify(ids) : null },
      });
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const selected = attached.map((a) => String(a.id));

  return (
    <Card className="p-3 space-y-3">
      <h2 className="font-semibold">Agents using this provider</h2>
      {!armedCount && (
        <Alert variant="warning">
          None of this provider’s tools are armed yet, so attaching it gives an agent nothing.
          Approve and enable the tools below first.
        </Alert>
      )}
      <Can module="agents" action="govern">
        <Select
          label="Attached agents"
          multiSelect
          searchable
          allowClear
          disabled={busy}
          options={agents.map((a) => ({ value: String(a.id), label: a.name }))}
          value={selected}
          onChange={(next) => {
            const nextIds = (Array.isArray(next) ? next : []).map(String);
            const added = agents.find((a) => nextIds.includes(String(a.id)) && !selected.includes(String(a.id)));
            const dropped = agents.find((a) => selected.includes(String(a.id)) && !nextIds.includes(String(a.id)));
            if (added) setAttachment(added, true);
            else if (dropped) setAttachment(dropped, false);
          }}
        />
      </Can>
      {attached.length === 0
        ? <p className="text-sm text-secondary-500">No agent uses this provider yet. Attach it so agents can use its tools.</p>
        : (
          <ul className="text-sm space-y-1">
            {attached.map((a) => (
              <li key={a.id}>
                <a className="text-primary-600 hover:underline" href={`/app-ai-agent/${a.id}`}>{a.name}</a>
                <span className="text-secondary-500 text-xs"> · {a.key}</span>
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
}

/**
 * Run one tool for real, right here.
 *
 * The only way to distinguish a provider that lists its tools from one that will actually
 * serve them, per tool. Destructive tools need an explicit tick — the server refuses without
 * `confirm: true`, and this mirrors that rather than trusting the button label.
 */
function TryToolCell({ providerId, tool }) {
  const { fetchApi } = useAppApi();
  const [open, setOpen] = useState(false);
  const [args, setArgs] = useState('{}');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    let parsed;
    try {
      parsed = JSON.parse(args || '{}');
    } catch {
      setResult({ isError: true, text: 'Arguments must be valid JSON.' });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      setResult(await fetchApi('providerToolTry', { providerId, toolId: tool.id, arguments: parsed, confirm: confirmed }));
    } catch (ex) {
      setResult({ isError: true, text: ex?.response?.data?.error || ex.message });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Try it</Button>;
  }
  return (
    <div className="min-w-[240px] space-y-2">
      <textarea
        className="w-full h-20 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-xs font-mono"
        value={args}
        spellCheck={false}
        onChange={(e) => setArgs(e.target.value)}
        aria-label={`Arguments for ${tool.name}`}
      />
      {!!tool.destructive && (
        <label className="flex items-center gap-2 text-[11px] text-red-600">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          This tool is destructive — run it for real
        </label>
      )}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || (!!tool.destructive && !confirmed)} onClick={run}>
          {busy ? 'Running…' : 'Run'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => { setOpen(false); setResult(null); }}>Close</Button>
      </div>
      {result && (
        <pre className={`whitespace-pre-wrap text-[11px] max-h-40 overflow-auto ${result.isError ? 'text-red-600' : 'text-secondary-600'}`}>
          {result.text}
        </pre>
      )}
    </div>
  );
}

/** One place for the group a row belongs to — the filter, the badge and the sort all use it. */
function groupOf(t) {
  return t.meta_group || t.meta_category || 'Ungrouped';
}

/** MCP `execution.taskSupport` ("forbidden" | "optional" | "required") in words. */
const TASK_SUPPORT_LABELS = {
  forbidden: 'No background tasks',
  optional: 'Background task optional',
  required: 'Runs as a background task',
};

/** twreact-ui <Table> stamps `whitespace-nowrap px-6` on every cell, so long descriptions
 *  ran straight over the Group and Category columns and pushed the switches off-screen.
 *  Merged with tailwind-merge upstream, so these win. */
const MCP_CELL_CLASS = () => 'whitespace-normal align-top px-3 py-3';

function CategoryCell({ t, busy, changeCategory }) {
  if (isAskTool(t)) {
    return (
      <div className="min-w-[160px] max-w-[210px]">
        <Badge variant="secondary" size="sm">ask</Badge>
        <p className="text-[11px] text-secondary-500 mt-1">{ASK_HELP}</p>
      </div>
    );
  }
  return (
    <div className="min-w-[160px] max-w-[210px]">
      <Select value={t.category} disabled={busy} onChange={(v) => changeCategory(t, v)} options={CATEGORY_OPTIONS} allowClear={false} />
      <p className="text-[11px] text-secondary-500 mt-1">{CATEGORY_HELP[t.category]}</p>
      {isAutoWrite(t) && <AutoWriteNote />}
      {!!t.destructive && <DestructiveNote />}
    </div>
  );
}

function GroupCell({ t }) {
  const kind = t.meta_kind ? `/${t.meta_kind}` : '';
  const task = t.execution_task_support;
  const openWorld = t.open_world_hint == null ? null : Number(t.open_world_hint) === 1 || t.open_world_hint === true;
  return (
    <div className="min-w-[110px] space-y-0.5">
      <Badge variant="secondary" size="sm">{t.group_label}{kind}</Badge>
      {task && (
        <div className="text-[10px] text-secondary-500" title={`MCP execution.taskSupport: ${task}`}>
          {TASK_SUPPORT_LABELS[task] || `Tasks: ${task}`}
        </div>
      )}
      {openWorld != null && (
        <div className="text-[10px] text-secondary-400" title="MCP openWorldHint — whether the tool reaches outside this server">
          {openWorld ? 'Open world' : 'Closed world'}
        </div>
      )}
    </div>
  );
}

/** Long snake_case names wrap at underscores instead of mid-word. */
function BreakableName({ name }) {
  const parts = String(name).split('_');
  return parts.map((part, i) => (
    // Name segments never reorder, so the index is a stable key.
    <span key={i}>{part}{i < parts.length - 1 && <>_<wbr /></>}</span>
  ));
}

function mcpToolColumns({ provider, busyId, patch, changeCategory }) {
  return [
    {
      key: 'name',
      label: 'Tool',
      render: (t) => (
        <div className="min-w-[200px] max-w-md">
          <div className="font-mono text-sm break-words"><BreakableName name={`${provider.slug}_${t.name}`} /></div>
          <div className="text-xs text-secondary-500 break-words line-clamp-3" title={t.description}>{t.description}</div>
          {t.title && t.title !== t.name && <div className="text-[11px] text-secondary-400">title: {t.title}</div>}
          {t.duplicate_count > 1 && (
            <div className="text-[11px] text-amber-600">Listed {t.duplicate_count}× — reconnect to merge</div>
          )}
        </div>
      ),
    },
    {
      key: 'group_label',
      label: 'Group',
      searchable: false,
      render: (t) => <GroupCell t={t} />,
    },
    {
      key: 'category',
      label: 'Category',
      searchable: false,
      render: (t) => <CategoryCell t={t} busy={busyId === t.id} changeCategory={changeCategory} />,
    },
    {
      key: 'approved',
      label: 'Approved',
      searchable: false,
      render: (t) => <Switch checked={!!t.approved} disabled={busyId === t.id} onChange={(checked) => patch(t, { approved: checked })} />,
    },
    {
      key: 'enabled',
      label: 'Enabled',
      searchable: false,
      render: (t) => (
        <Switch
          checked={!!t.enabled}
          disabled={busyId === t.id || !t.approved}
          onChange={(checked) => patch(t, { enabled: checked })}
        />
      ),
    },
    {
      key: 'run_count',
      label: 'Runs',
      searchable: false,
      render: (t) => (
        <span className="text-xs text-secondary-500 whitespace-nowrap" title={t.run_count ? `${t.success_count || 0} of ${t.run_count} run(s) succeeded` : undefined}>
          {t.run_count ? `${t.success_count || 0}/${t.run_count} ok` : 'never run'}
        </span>
      ),
    },
    {
      key: 'try',
      label: 'Test',
      searchable: false,
      sortable: false,
      render: (t) => <TryToolCell providerId={provider.id} tool={t} />,
    },
  ];
}

// ---- Main unified detail ----

// eslint-disable-next-line max-lines-per-function, complexity
export default function ToolProviderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { fetchApi } = useAppApi();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [syncResult, setSyncResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('All');

  const reload = useCallback(() => fetchApi('toolProviderShow', id)
    .then(setData)
    .catch((ex) => setErr(ex?.response?.data?.error || ex.message)), [id]);

  const loadAgents = useCallback(() => fetchApi('crud', { model: 'AppAiAgent', action: 'list', limit: 200 })
    .then((res) => setAgents((Array.isArray(res) ? res : res?.data) || []))
    .catch(() => setAgents([])), []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  // Also reload agents when provider kind is mcp (for MCP-specific panels); for http/client still useful
  const tools = data?.tools || [];
  const isMcp = data?.provider?.kind === 'mcp';
  const armedCount = useMemo(() => tools.filter((t) => t.approved && t.enabled).length, [tools]);
  // Same tool name stored more than once (an overlapping-sync race) — surfaced so the
  // table doesn't silently show every tool twice; a reconnect merges them server-side.
  const nameCounts = useMemo(() => {
    const m = new Map();
    for (const t of tools) m.set(t.name, (m.get(t.name) || 0) + 1);
    return m;
  }, [tools]);
  const duplicateRows = useMemo(
    () => [...nameCounts.values()].reduce((n, c) => n + (c > 1 ? c - 1 : 0), 0),
    [nameCounts],
  );
  const groups = useMemo(() => {
    const s = new Set(tools.map(groupOf));
    return ['All', ...Array.from(s).sort()];
  }, [tools]);
  const mcpRows = useMemo(() => {
    let rows = applyFilter(tools, filter);
    if (groupFilter !== 'All') rows = rows.filter((t) => groupOf(t) === groupFilter);
    return rows.map((t) => ({ ...t, group_label: groupOf(t), duplicate_count: nameCounts.get(t.name) || 1 }));
  }, [tools, filter, groupFilter, nameCounts]);
  // A group that vanished after a reload (tools re-synced) would leave the table empty
  // behind a filter the dropdown can no longer show.
  useEffect(() => {
    if (groupFilter !== 'All' && tools.length && !groups.includes(groupFilter)) setGroupFilter('All');
  }, [groups, groupFilter, tools.length]);
  // Row selection scoping the bulk menu: ticked rows act on the ticked ids, unticked acts
  // on every row matching the table. `visibleRef` is what DataTable reports back through
  // onFilteredRows — post-search rows across all pages. Pruned when the provider reloads.
  const [selectedIds, setSelectedIds] = useState([]);
  const visibleRef = useRef([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const handleVisibleRows = useCallback((rows) => {
    visibleRef.current = rows;
    setVisibleCount(rows.length);
  }, []);
  const clearSelection = useCallback(() => setSelectedIds([]), []);
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.length) return prev;
      const alive = new Set((data?.tools || []).map((t) => t.id));
      const next = prev.filter((id) => alive.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [data]);
  const { busyId, patch, changeCategory } = useToolActions(id, { onChanged: reload, onError: setErr });

  const sync = async () => {
    setErr(''); setNotice(''); setSyncResult(null); setSyncing(true);
    try {
      const res = await fetchApi('toolProviderSync', id);
      if (isMcp) {
        setSyncResult(res);
      } else if (data?.provider?.kind === 'a2a') {
        const card = res.card || {};
        const drift = res.drifted ? ' The card changed since it was approved — review and re-arm the tools.' : '';
        setNotice(`Fetched the Agent Card for "${card.name || 'the agent'}" (${(card.skills || []).length} skill(s)).${drift} New or changed tools need approving before they can be enabled.`);
      } else {
        const dropped = res.rejected?.length ? ` ${res.rejected.length} declaration(s) were unusable.` : '';
        setNotice(`Found ${res.discovered} tool(s).${dropped} New or changed ones need approving before they can be enabled.`);
      }
      await reload();
      await loadAgents();
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return err ? <Alert variant="error">{err}</Alert> : null;
  const { provider } = data;

  // Controlled checkbox column — the library's own `selectable` is page-only and
  // uncontrolled, which can't express "all matching across pages". `sortable: false` keeps
  // the header checkbox from toggling a sort; `searchable: false` keeps the column out of
  // the search keys (see components/dataTableFilter.js).
  const toggleOneMcp = (toolId) => setSelectedIds((prev) => (
    prev.includes(toolId) ? prev.filter((v) => v !== toolId) : [...prev, toolId]
  ));
  const toggleAllVisibleMcp = () => {
    const ids = visibleRef.current.map((t) => t.id);
    setSelectedIds((prev) => (
      ids.length && ids.every((v) => prev.includes(v))
        ? prev.filter((v) => !ids.includes(v))
        : [...new Set([...prev, ...ids])]
    ));
  };
  const allVisibleChecked = visibleCount > 0
    && visibleRef.current.every((t) => selectedIds.includes(t.id));
  const selectColumn = {
    key: '__select',
    label: (
      <input
        type="checkbox"
        className="rounded border-gray-300"
        checked={allVisibleChecked}
        onChange={toggleAllVisibleMcp}
        aria-label="Select all matching tools"
        title="Select all matching tools"
      />
    ),
    searchable: false,
    sortable: false,
    render: (t) => (
      <input
        type="checkbox"
        className="rounded border-gray-300"
        checked={selectedIds.includes(t.id)}
        onChange={() => toggleOneMcp(t.id)}
        aria-label={`Select ${t.name}`}
      />
    ),
  };
  const mcpColumns = isMcp ? [selectColumn, ...mcpToolColumns({ provider, busyId, patch, changeCategory })] : [];
  let syncLabel = null;
  if (provider.manifest_synced_at) syncLabel = `Last synced ${new Date(provider.manifest_synced_at).toLocaleString()}`;
  else if (provider.kind === 'mcp' || provider.kind === 'a2a') syncLabel = 'Never synced';

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title={provider.name}
        subtitle={(
          <>
            <p className="text-secondary-500 text-sm flex flex-wrap items-center gap-2">
              <span className="font-mono">{provider.slug}_*</span>
              <Badge variant="primary" size="sm">{KIND_LABELS[provider.kind]?.label || provider.kind}</Badge>
              {provider.base_url ? <span className="break-all">{provider.base_url}</span> : null}
              {provider.kind === 'a2a' && provider.endpoint_url && (
                <Badge variant="secondary" size="sm" title="The JSON-RPC endpoint the pinned Agent Card advertises — calls go here.">
                  {provider.endpoint_url}
                </Badge>
              )}
              {provider.kind === 'mcp' && provider.transport_mode && (
                <Badge variant="secondary" size="sm" title="Which protocol this server turned out to speak — detected on the first successful sync.">
                  {TRANSPORT_LABELS[provider.transport_mode] || provider.transport_mode}
                </Badge>
              )}
            </p>
            <p className="text-xs text-secondary-500 mt-1">
              {syncLabel}
              {provider.kind === 'mcp' ? ` · ${armedCount} of ${tools.length} tool(s) armed` : null}
              {provider.kind === 'mcp' && tools.length > 0 ? ` · ${tools.filter((t) => t.embedding_json).length}/${tools.length} indexed` : null}
            </p>
            {provider.kind === 'mcp' && armedCount > 0 && tools.some((t) => t.approved && t.enabled && !t.embedding_json) && (
              <p className="text-xs text-amber-600 mt-1">
                Some armed tools have no semantic index — they rank last in turn-time selection. Re-sync to rebuild it.
              </p>
            )}
          </>
        )}
        actions={(
          <div className="flex gap-2">
            {provider.kind === 'mcp' && (
              <Can module="toolproviders" action="author">
                <Button size="sm" onClick={sync} disabled={syncing}>
                  {syncing ? 'Reconnecting…' : 'Reconnect & refresh tools'}
                </Button>
              </Can>
            )}
            {provider.kind === 'a2a' && (
              <Can module="toolproviders" action="author">
                <Button size="sm" onClick={sync} disabled={syncing}>
                  {syncing ? 'Fetching card…' : 'Fetch Agent Card & refresh tools'}
                </Button>
              </Can>
            )}
            <Button size="sm" variant="secondary" onClick={() => nav('/app-tool-provider')}>Back</Button>
          </div>
        )}
      />

      {provider.is_platform && (
        <Alert variant="info" title="Platform-wide provider">
          These are the platform&apos;s own first-party tools, seeded once and shared by every
          company (<code>commands/seed-platform-tools.js</code>). It is shown here so the belt
          is auditable — arming or revoking one would change it for every tenant, so the
          server refuses edits from this screen. Change the registry and re-run the seed.
        </Alert>
      )}
      {err && <Alert variant="error" dismissible onDismiss={() => setErr('')}>{err}</Alert>}
      {notice && <Alert variant="info" dismissible onDismiss={() => setNotice('')}>{notice}</Alert>}
      {isMcp && <SyncReport result={syncResult} onDismiss={() => setSyncResult(null)} providerId={id} tools={tools} onChanged={reload} />}

      {provider.status === 'error' && provider.last_error && (
        <Alert variant="error" title="The last connection attempt failed">{provider.last_error}</Alert>
      )}
      {provider.status === 'drifted' && (
        <Alert variant="warning" title="The remote manifest changed since it was approved">
          None of this provider&apos;s tools are bound to any agent while it is in this
          state. {isMcp ? 'Reconnect, then re-approve what changed' : 'Review what changed and re-approve each tool'} — a description is text the
          model reads as instructions, so a silent edit is not a cosmetic one.
        </Alert>
      )}
      {provider.kind === 'a2a' && (
        <Alert variant="info" title="A remote A2A agent">
          Its tools come from the pinned Agent Card: <code>{provider.slug}_ask</code> delegates a
          message and lands gated and destructive, so it cannot run until you review it.
          Un-mark destructive to let the approval gate decide each call, or pre-approve it on an
          agent&apos;s Actions. <code>{provider.slug}_get_task</code> only reads. Whatever the remote agent
          answers is shown to the model as data from that agent, never as instructions.
        </Alert>
      )}
      {provider.kind === 'client' && (
        <Alert variant="info" title="Declarations come from your signed manifest">
          For a browser provider the tool list is whatever your backend signs at runtime
          (<code>typ: &apos;agent.tools&apos;</code>), not rows stored here. This provider
          exists to bind that manifest to an embed key and to give you a kill switch. See
          docs/PUBLIC-API.md.
        </Alert>
      )}

      {/* MCP: agents using this provider + richer tool management */}
      {isMcp && (
        <>
          <AgentsPanel id={id} agents={agents} armedCount={armedCount} onChanged={loadAgents} onError={setErr} />

          <Card className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-semibold">Tools ({tools.length})</h2>
              <Can module="toolproviders" action="author">
                <BulkArmBar
                  tools={tools}
                  providerId={id}
                  selectedIds={selectedIds}
                  visibleIds={visibleRef.current.map((t) => t.id)}
                  onChanged={reload}
                  onError={setErr}
                  onClearSelection={clearSelection}
                />
              </Can>
            </div>
            {duplicateRows > 0 && (
              <Alert variant="warning" className="mb-3" title={`${duplicateRows} duplicate tool row(s)`}>
                Some tools are stored more than once, so they appear twice here, in the system
                prompt and in an agent&apos;s tool belt. Use <strong>Reconnect &amp; refresh tools</strong> to
                merge them — the copy matching the server&apos;s current manifest is kept.
              </Alert>
            )}
            <DataTable
              data={mcpRows}
              cellClass={MCP_CELL_CLASS}
              columns={mcpColumns}
              emptyText={tools.length ? 'No tools match this filter.' : 'No tools discovered yet — reconnect to fetch them.'}
              search={{ placeholder: 'Search tools…', keys: ['name', 'description', 'meta_group', 'meta_category', 'meta_kind'] }}
              pageSize={25}
              onReload={reload}
              onFilteredRows={handleVisibleRows}
              toolbar={(
                <div className="flex flex-wrap gap-2">
                  <div className="w-full sm:w-56">
                    <Select aria-label="Filter by state" allowClear={false} options={FILTERS} value={filter} onChange={(v) => setFilter(v || 'all')} />
                  </div>
                  <div className="w-full sm:w-48">
                    <Select
                      aria-label="Filter by group"
                      allowClear={false}
                      options={groups.map((g) => ({ value: g, label: g === 'All' ? 'All groups' : g }))}
                      value={groupFilter}
                      onChange={(v) => setGroupFilter(v || 'All')}
                    />
                  </div>
                </div>
              )}
            />
          </Card>

          <ToolGraphCard providerId={id} />

          <SystemPromptPanel id={id} agents={agents} onAttachError={setErr} onAgentsChanged={loadAgents} />
        </>
      )}

      {/* Shared panels: auth + secret + tool table (layout differs for MCP).
          Platform providers have no outbound endpoint and no signing secret — their
          gate surface is the tool table alone. */}
      {!isMcp && provider.kind !== 'client' && provider.kind !== 'platform' && (
        <OutboundAuthPanel providerId={id} provider={provider} onChanged={reload} onError={setErr} />
      )}
      {!isMcp && provider.kind !== 'platform' && <SecretPanel providerId={id} hasSecret={provider.has_secret} onError={setErr} />}

      {!isMcp && provider.kind !== 'client' && (
        <ToolTable tools={tools} providerId={id} onChanged={reload} onError={setErr} />
      )}

      {/* For MCP, auth/secret come after tools per former McpServerDetail order */}
      {isMcp && (
        <>
          <OutboundAuthPanel providerId={id} provider={provider} onChanged={reload} onError={setErr} />
          <SecretPanel providerId={id} hasSecret={provider.has_secret} onError={setErr} />
        </>
      )}

      {provider.kind === 'http' && (
        <Can module="toolproviders" action="author">
          <AddToolForm providerId={id} onAdded={reload} onError={setErr} />
        </Can>
      )}
    </PageShell>
  );
}
